import { Loader2, MapPin, Pencil, Star, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  type CustomerAddress,
  useCustomerAddresses,
  useDeleteCustomerAddress,
  useSetDefaultCustomerAddress,
} from "@/features/customer/hooks/use-customer-addresses";

export default function AddressesListPage() {
  const { t } = useTranslation();
  const addressesQuery = useCustomerAddresses();
  const setDefault = useSetDefaultCustomerAddress();
  const deleteAddress = useDeleteCustomerAddress();

  const handleSetDefault = async (id: string) => {
    try {
      await setDefault.mutateAsync(id);
      toast.success(t("addresses.list.setDefaultSuccess", "Domicilio predeterminado"));
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const handleDelete = async (address: CustomerAddress) => {
    const confirmed = window.confirm(
      t("addresses.list.deleteConfirm", `¿Eliminar "${address.label}"?`),
    );
    if (!confirmed) {
      return;
    }
    try {
      await deleteAddress.mutateAsync(address.id);
      toast.success(t("addresses.list.deleteSuccess", "Domicilio eliminado"));
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  if (addressesQuery.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (addressesQuery.isError) {
    return <div className="text-destructive">{(addressesQuery.error as Error).message}</div>;
  }

  const addresses = addressesQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t("addresses.list.title", "Mis domicilios")}</h1>
        <Button asChild>
          <Link to="/account/addresses/new">{t("addresses.list.add", "Agregar domicilio")}</Link>
        </Button>
      </div>

      {addresses.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <MapPin className="h-8 w-8 text-muted-foreground" />
            <p className="text-muted-foreground">
              {t(
                "addresses.list.empty",
                "Aún no registraste un domicilio. Agregá uno para pedir delivery.",
              )}
            </p>
            <Button asChild>
              <Link to="/account/addresses/new">
                {t("addresses.list.add", "Agregar domicilio")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-3">
          {addresses.map((address) => (
            <li key={address.id}>
              <Card>
                <CardContent className="flex items-start justify-between gap-3 p-4">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{address.label}</span>
                      {address.isDefault ? (
                        <Badge variant="secondary">
                          {t("addresses.list.default", "Predeterminado")}
                        </Badge>
                      ) : null}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {address.line1}
                      {address.line2 ? `, ${address.line2}` : ""}
                      {address.city ? ` — ${address.city}` : ""}
                    </p>
                    {address.notes ? (
                      <p className="text-xs text-muted-foreground">{address.notes}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    {address.isDefault ? null : (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        title={t("addresses.list.setDefault", "Hacer predeterminado")}
                        onClick={() => handleSetDefault(address.id)}
                        disabled={setDefault.isPending}
                      >
                        <Star className="h-4 w-4" />
                      </Button>
                    )}
                    <Button asChild size="icon" variant="ghost">
                      <Link to={`/account/addresses/${address.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-destructive"
                      title={t("addresses.list.delete", "Eliminar")}
                      onClick={() => handleDelete(address)}
                      disabled={deleteAddress.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
